import * as React from 'react';
import { VisuallyHidden } from 'radix-ui';
import { ArrowLeft } from 'lucide-react';

import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../ui/sheet';
import { cn } from '../../lib/utils';

export interface DetailSheetProps {
  /** Whether the sheet is open. */
  open: boolean;
  /** Called when the sheet should change open state (e.g. user pressed escape or clicked overlay). */
  onOpenChange: (open: boolean) => void;
  /**
   * Accessible title for the sheet. Rendered visually hidden because the
   * sheet's own header bar carries the visible labelling — this satisfies
   * Radix's a11y contract without forcing a duplicated heading.
   */
  title: string;
  /** Accessible description for the sheet. Rendered visually hidden. */
  description?: string;
  /**
   * When set, renders a "back" button at the start of the header bar
   * that calls this handler. Use the same affordance the caller wants
   * the back action to perform (typically the same as closing the
   * sheet, but consumers can wire it however they like).
   */
  onBack?: () => void;
  /** Optional label override for the back button. Defaults to "Back". */
  backLabel?: string;
  /**
   * Optional slot rendered on the trailing edge of the header bar —
   * good for "Open full page" links, badges, or secondary actions.
   */
  headerExtra?: React.ReactNode;
  /**
   * Optional class names appended to the SheetContent. Use this to
   * override the default width (`md:w-[70vw] lg:w-[60vw] xl:w-[55vw]`).
   */
  className?: string;
  /** Body content. Rendered inside a scrollable region below the header. */
  children: React.ReactNode;
}

/**
 * Slide-over panel for showing detail content without navigating away
 * from the current page. Used by `FlowRunAgentSheet` (drill into an
 * agent from a flow run) and the org-setup wizard (preview a plugin
 * during onboarding) — anywhere a full-page navigation would be
 * disruptive or blocked.
 *
 * Renders a right-side `Sheet` with a small header bar (optional back
 * button + optional extra slot) and a scrollable body. A11y title and
 * description are rendered visually hidden because the sheet relies on
 * the consumer's content (or the back button label) for visible
 * labelling.
 */
export function DetailSheet({
  open,
  onOpenChange,
  title,
  description,
  onBack,
  backLabel = 'Back',
  headerExtra,
  className,
  children,
}: DetailSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className={cn(
          'flex h-full w-full flex-col gap-0 p-0',
          'sm:max-w-none md:w-[70vw] lg:w-[60vw] xl:w-[55vw]',
          className,
        )}
      >
        <VisuallyHidden.Root>
          <SheetTitle>{title}</SheetTitle>
          {/* Radix's Dialog (which Sheet wraps) emits a console warning
              when DialogContent has no Description or aria-describedby.
              Always render a SheetDescription — fall back to the title
              when the caller doesn't supply one. */}
          <SheetDescription>{description ?? title}</SheetDescription>
        </VisuallyHidden.Root>
        {(onBack || headerExtra) && (
          <div
            data-slot="detail-sheet-header"
            className="flex items-center gap-2 border-b bg-card px-3 py-2"
          >
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                aria-label={backLabel}
              >
                <ArrowLeft className="h-4 w-4" />
                {backLabel}
              </button>
            )}
            {headerExtra && <div className="ml-auto">{headerExtra}</div>}
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto" data-slot="detail-sheet-body">
          {children}
        </div>
      </SheetContent>
    </Sheet>
  );
}
